const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/actions/kisapi/workItems.ts';
import { compileEndpoint } from './utils';

// A workItem's true DB fields excluding CONTENT
const WORKITEM_STD_FIELDS = [
    'DAT',
    'DONEDAT',
    'DONEVIS',
    'FALLNR',
    'FCODE',
    'KLINIK',
    'LASTDAT',
    'LAST_MODIFIED',
    'NEXTRESULT',
    'PATNAME',
    'PATNR',
    'PFLAG',
    'PRIO',
    'REFID',
    'RESULT',
    'RKAT',
    'RKEY',
    'SEENDAT',
    'SEENVIS',
    'SKAT',
    'SKEY',
    'STARTDAT',
    'STATUS',
    'STOPDAT',
    'SUBJECT',
    'TKAT',
    'TKEY',
    'VISUM',
    'WORKID',
];

// All fields the workItem endpoint may return including CONTENT and additional derived fields
export const WORKITEMDATA_REQ_FIELDS = WORKITEM_STD_FIELDS.concat(['CONTENT', 'ETAG', 'RIGHTS']);

/**
 * Loads work items (e.g. station messages) for a certain patient withing an optionally given time span.
 * @param patnr patient number
 * @param tkat type of workitem, primary key
 * @param tkey type of workitem, secondary key
 * @param rkat recipient, primary key
 * @param rkey recipient, secondary key
 * @param skat sender, primary key
 * @param skey sender, secondary key
 * @param status
 * @param fcode
 * @param mindat start of time span; YYYY-MM-DD HH:mm:ss
 * @param maxdat end of time span; YYYY-MM-DD HH:mm:ss
 * @param fields comma-separated list of record members to include in the response, may be null
 * @param disallowArrays
 * @param withRights true=also load the object's various access rights
 * @param withContent specifies how the CONTENT should be loaded:
 * @param transformRtf
 *   true=load the full content
 *   false=don't load the CONTENT
 *   string[]=return only the given Qscript-paths of the CONTENT (path elements delimited by slash)
 */
export function apiLoadWorkItems(
    patnr: string,
    tkat: string | undefined,
    tkey: string | undefined,
    rkat: string | undefined,
    rkey: string | undefined,
    skat: string | undefined,
    skey: string | undefined,
    status: string | null | undefined,
    fcode: string | null | undefined,
    mindat: string | undefined,
    maxdat: string | undefined,
    fields: string | undefined,
    disallowArrays: boolean = false,
    withRights: boolean = true,
    withContent: boolean | string[] = true,
    transformRtf: boolean = true
) {
    if (!fields) {
        fields = WORKITEMDATA_REQ_FIELDS.filter((field: string) => {
            if (!withRights && field === 'RIGHTS') {
                return false;
            } else if (!withContent && field === 'CONTENT') {
                return false;
            } else if (Array.isArray(withContent) && !withContent.length && field === 'CONTENT') {
                return false;
            } else {
                return true;
            }
        }).join(',');
    }

    const contentPaths = Array.isArray(withContent) && withContent.length > 0 && withContent.join(',');

    let requestConfig: any;

    if (disallowArrays) {
        requestConfig = {
            headers: { Accept: 'array=no' },
        };
    }

    return {
        endpoint: compileEndpoint(
            ['WorkItems'],
            [
                { key: 'PATNR', value: patnr },
                mindat && { key: 'MINDAT', value: mindat },
                maxdat && { key: 'MAXDAT', value: maxdat },
                tkat && { key: 'TKAT', value: tkat },
                tkey && { key: 'TKEY', value: tkey },
                skat && { key: 'SKAT', value: skat },
                skey && { key: 'SKEY', value: skey },
                rkat && { key: 'RKAT', value: rkat },
                rkey && { key: 'RKEY', value: rkey },
                status && { key: 'STATUS', value: status },
                fcode && { key: 'FCODE', value: fcode },
                contentPaths && { key: 'ContentPaths', value: contentPaths },
                { key: 'MaxResultRows', value: '100' },
                { key: 'Fields', value: fields },
                transformRtf && { key: 'Trf', value: 'RtfToAnsi' },
            ]
        ),
        requestConfig,
    };
}

/**
 * Loads a WorkItem record
 * @param workId id of the WorkItem
 * @param fields optional; desired fields in response; default = all (except CONTENT if withContent is false)
 * @param fullTree true=walk REFIDs to find all WorkItems in the same WorkItem thread
 * @param disallowArrays true=never try to convert Qstructs to arrays
 * @param withContent true=include CONTENT in response
 */
export function apiLoad1WorkItem(
    workId: string,
    fields?: string[],
    fullTree?: boolean,
    disallowArrays: boolean = false,
    withContent: boolean = true
) {
    let theFields = !fields ? WORKITEM_STD_FIELDS.join(',') : fields.join(',');
    if (withContent && !fields?.includes('CONTENT')) theFields += ',CONTENT';

    let requestConfig: any;

    if (disallowArrays) {
        requestConfig = {
            headers: { Accept: 'array=no' },
        };
    }

    return {
        endpoint: compileEndpoint(
            ['WorkItems', workId, !fullTree ? null : 'Tree'],
            [
                !fullTree ? undefined : { key: 'MaxResultRows', value: '100' }, //
                { key: 'Fields', value: theFields },
            ]
        ),
        requestConfig,
    };
}

/**
 * Loads minimum set of data for a work item (in order to get the etag).
 */
export function apiGetWorkItemsEtag(workid: string) {
    return {
        endpoint: compileEndpoint(
            ['WorkItems', workid], //
            [{ key: 'Fields', value: 'WORKID' }]
        ),
    };
}

/**
 * Loads rights for a work item
 */
export function apiGetWorkItemsRights(workid: string) {
    return {
        endpoint: compileEndpoint(
            ['WorkItems', workid, 'Rights'], //
            null
        ),
    };
}

/**
 * Updates or creates a WorkItem.
 *
 * NOTE: WorkItems were originally designed to be immutable (like mails in a mailbox). Thus, the only members that may
 * be updated are STATUS and STOPDAT. Attempting to update any other member will result in a new WorkItem being created
 * instead, connected through REFID-WORKID relation. This connection can be depicted like the relationship of an
 * original mail and its reply or forwarded mail.
 *
 * To create a new, unconnected WorkItem:
 *    - Pass null to both workid and etag
 *    - Include at least FCODE, TKAT and TKEY in the body
 *    - Don't include WORKID in body
 *
 * To create a new WorkItem that's connected to some other item:
 *    - Pass the previous item's values to workid and etag
 *    - Include all members that should be changed with respect to the previous item in the body
 *    - Include WORKID in the body
 *    - Include at least one member other than WORKID, STATUS and STOPDAT in the body. (This peculiar requirement
 *      enforces that a new WorkItem will be created.)
 *    - When including STATUS or RESULT in body, unlike other members, they will be set in the previous WorkItem rather
 *      than in the new one.
 *
 * To update a WorkItem's STOPDAT:
 *    - Pass the previous item's values to workid and etag
 *    - Pass an object containing { WORKID, STOPDAT } as body. Don't include anything else as this may prevent update.
 *
 * To update a WorkItem's STATUS:
 *    - Pass the previous item's values to workid and etag
 *    - Pass an object containing { WORKID, STATUS } as body. Don't include anything else as this may prevent update.
 *
 * When updating STATUS, please note the meaning of the values:
 *    - U=unopened/unread (a WorkItem's initial STATUS; may not be set explicitly)
 *    - O=opened/read (STATUS will automatically change from U to O when a user views the item; may not be set explic.)
 *    - D=done (the work associated with this WorkItem is marked as done)
 *    - R=replied (used for mail-like WorkItems when a user replied to this item)
 *    - F=forwarded (used for mail-like WorkItems when a user forwarded this item)
 *    - I=invalid (similar to 'D', but this will also hide the item in the normal views)
 *    - Y=recycled (used instead of 'O' when an item that had status 'I' was reactivated)
 *
 * Setting STATUS to 'D', 'R' or 'F' may also have the following side effects:
 *    - STOPDAT will be set to the current time
 *    - DONEDAT will be set to the current time
 *    - DONEVIS will be set to the user's VISUM
 *
 * When updating CONTENT, keep in mind:
 *    - There are various top-level keys that will be interpreted by KISAPI. To avoid unexpected behaviour, start your
 *      own keys with lowercase letters as this is rare in QSCRIPT.
 *
 * @param body new record (should contain only fields that have changed / necessary for creating a work item)
 * @param workid workid of the item that should be updated (null/undefined to create new item)
 * @param etag used for safe update (null/undefined to create new item)
 * @param getNewWorkItemETag specifies whether to return the newly created item's etag (true) or the previous item's
 *    etag after update.
 * @param stopFormer specifies how to update the previous item's STOPDAT when creating a new, connected item
 */
export function apiPostWorkItem(
    body: any,
    workid?: string,
    etag?: string,
    getNewWorkItemETag: boolean = true,
    stopFormer: 'default' | 'keep' | 'start-of-new' = 'default'
) {
    // Due to the endpoint's poor design, we have to hack some of the parameters into the CONTENT.
    // At least the endpoint is clever enough to remove them from CONTENT before storing the blob on DB...
    // FIXME this endpoint should be rewritten from scratch, this is unacceptable hackery! TMOB-2650
    switch (stopFormer) {
        case 'keep':
            body = { ...body, CONTENT: { ...body?.CONTENT, KeepStopDat: '1' } };
            break;
        case 'start-of-new':
            if (!body?.STATUS) {
                // For some reason, StopOnNewStart doesn't do anything when body doesn't contain some STATUS...
                throw new Error('Argument start-of-new requires a non-empty STATUS in body!');
            }
            body = {
                ...body,
                CONTENT: {
                    ...body?.CONTENT,
                    StopOnNewStart: '1',
                    ForceStatus: '1', // because otherwise StopOnNewStart doesn't do anything when STATUS is 'D'
                },
                STATUS: body?.STATUS,
            };
            break;
    }

    return {
        endpoint: compileEndpoint(
            ['WorkItems', etag && workid ? workid : null], //
            [(getNewWorkItemETag && { key: 'GetNewWorkItemETag', value: '1' }) || undefined]
        ),
        requestConfig: {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                ...(etag && workid ? { 'If-Match': etag } : {}),
            },
            body: JSON.stringify(body),
        },
    } as const;
}

export function apiSaveSignedPDF(workid: string, signedPdf: string) {
    return {
        endpoint: compileEndpoint(['KisMobile', 'MobUntschr'], []),
        requestConfig: {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ Buffer: signedPdf, WORKID: workid }),
        },
    };
}
